import { toast, type ExternalToast } from 'sonner';

export interface ToastOptions extends ExternalToast {
  /**
   * The description text for the toast
   */
  description?: string;
}

/**
 * Custom hook for toast notifications
 * Provides typed methods for different toast types
 */
export const useToast = () => {
  const success = (message: string, options?: ToastOptions) => {
    return toast.success(message, options);
  };

  const error = (message: string, options?: ToastOptions) => {
    return toast.error(message, {
      duration: 10000,
      closeButton: true,
      ...options,
    });
  };

  const warning = (message: string, options?: ToastOptions) => {
    return toast.warning(message, options);
  };

  const info = (message: string, options?: ToastOptions) => {
    return toast.info(message, options);
  };

  const loading = (
    message: string,
    options?: Omit<ToastOptions, 'duration'>
  ) => {
    return toast.loading(message, options);
  };

  const promise = <T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: any) => string);
    }
  ) => {
    return toast.promise(promise, messages);
  };

  const dismiss = (id?: string | number) => {
    return toast.dismiss(id);
  };

  const custom = (
    jsx: (id: string | number) => React.ReactElement,
    options?: ToastOptions
  ) => {
    return toast.custom(jsx, options);
  };

  const message = (message: string, options?: ToastOptions) => {
    return toast(message, options);
  };

  return {
    success,
    error,
    warning,
    info,
    loading,
    promise,
    dismiss,
    custom,
    message,
    // Direct access to toast function
    toast,
  };
};

export default useToast;
